import React from 'react';
import { Link } from 'react-router-dom';

interface QuickStatsProps {
  userStats: {
    overall_progress: number;
    tests_completed: number;
    current_rank: number | string;
    study_time_hours: number;
  };
  subscriptionData?: {
    has_active_subscription: boolean;
    subscription_plan_key: string;
    subscription_plan_name: string;
    plan_color?: string;
    features?: {
      custom_quiz?: {
        enabled: boolean;
        limit: number | null;
        current_usage: number;
        remaining: number | null;
        unlimited: boolean;
        quota_exceeded: boolean;
      };
    };
  };
}

const QuickStats: React.FC<QuickStatsProps> = ({ userStats, subscriptionData }) => {
  // Helper function to get plan-specific colors
  const getPlanColors = () => {
    if (!subscriptionData) {
      return { primary: '#6c757d', secondary: '#6c757d15' };
    }

    const planKey = subscriptionData.subscription_plan_key?.toLowerCase();
    
    switch (planKey) {
      case 'free':
        return { primary: '#6c757d', secondary: '#6c757d15' }; // Gray
      case 'basic':
        return { primary: '#0d6efd', secondary: '#0d6efd15' }; // Blue
      case 'premium':
        return { primary: '#6f42c1', secondary: '#6f42c115' }; // Purple
      case 'pro':
        return { primary: '#fd7e14', secondary: '#fd7e1415' }; // Orange/Gold
      default:
        return { primary: subscriptionData.plan_color || '#6c757d', secondary: (subscriptionData.plan_color || '#6c757d') + '15' };
    }
  };

  // Helper function to get quota display text and styling
  const getQuotaDisplay = () => {
    if (!subscriptionData) {
      return { 
        text: 'Loading...', 
        subtext: 'Please wait',
        color: 'secondary', 
        icon: 'fas fa-spinner fa-spin',
        showUpgrade: false 
      };
    }

    const customQuiz = subscriptionData.features?.custom_quiz;
    const planColors = getPlanColors();
    
    if (!customQuiz) {
      return { 
        text: 'N/A', 
        subtext: 'Not Available',
        color: 'secondary', 
        icon: 'fas fa-question-circle',
        showUpgrade: false,
        customColor: planColors.primary
      };
    }

    const planName = subscriptionData.subscription_plan_name || 'Free';
    const planKey = subscriptionData.subscription_plan_key?.toLowerCase();

    // Unlimited plans (Basic, Premium, Pro)
    if (customQuiz.unlimited || customQuiz.limit === null || customQuiz.limit === -1) {
      return { 
        text: '∞', 
        subtext: `Unlimited • ${planName}`,
        color: 'success', 
        icon: 'fas fa-clipboard-list',
        showUpgrade: false,
        customColor: planColors.primary
      };
    }

    // Free plan with limits
    const remaining = customQuiz.remaining || 0;
    const total = customQuiz.limit || 20;
    
    if (customQuiz.quota_exceeded || remaining === 0) {
      return { 
        text: `0 / ${total}`, 
        subtext: `Limit Reached • ${planName}`,
        color: 'danger', 
        icon: 'fas fa-exclamation-triangle',
        showUpgrade: planKey === 'free',
        customColor: planColors.primary
      };
    }
    
    // Determine color based on remaining
    let alertLevel = 'info';
    if (remaining <= 2) alertLevel = 'danger';
    else if (remaining <= 5) alertLevel = 'warning';
    else if (remaining >= 15) alertLevel = 'success';
    
    return { 
      text: `${remaining} / ${total}`, 
      subtext: `${remaining} Left • ${planName}`,
      color: alertLevel, 
      icon: 'fas fa-clipboard-list',
      showUpgrade: planKey === 'free' && remaining <= 5,
      customColor: planColors.primary
    };
  };

  const quotaDisplay = getQuotaDisplay();
  const planColors = getPlanColors();

  return (
    <div className="row g-4 mb-4">
      <div className="col-lg-3 col-md-3 col-sm-6">
        <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '15px' }}>
          <div className="card-body text-center p-4">
            <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
              style={{ 
                width: '60px', 
                height: '60px',
                backgroundColor: '#667eea15',
                border: '2px solid #667eea'
              }}>
              <i className="fas fa-chart-line text-primary fa-lg"></i>
            </div>
            <h3 className="fw-bold mb-1">{userStats.overall_progress || 0}%</h3>
            <p className="text-muted mb-0">Overall Progress</p>
          </div>
        </div>
      </div>
      <div className="col-lg-3 col-md-3 col-sm-6">
        <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '15px' }}>
          <div className="card-body text-center p-4">
            <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
              style={{ 
                width: '60px', 
                height: '60px',
                backgroundColor: '#28a74515',
                border: '2px solid #28a745'
              }}>
              <i className="fas fa-trophy text-success fa-lg"></i>
            </div>
            <h3 className="fw-bold mb-1">{userStats.tests_completed || 0}</h3>
            <p className="text-muted mb-0">Tests Completed</p>
          </div>
        </div>
      </div>
      <div className="col-lg-3 col-md-3 col-sm-6">
        <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '15px' }}>
          <div className="card-body text-center p-4">
            <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
              style={{ 
                width: '60px', 
                height: '60px',
                backgroundColor: '#ffc10715',
                border: '2px solid #ffc107'
              }}>
              <i className="fas fa-medal text-warning fa-lg"></i>
            </div>
            <h3 className="fw-bold mb-1">#{userStats.current_rank || 'N/A'}</h3>
            <p className="text-muted mb-0">Current Rank</p>
          </div>
        </div>
      </div>
      <div className="col-lg-3 col-md-3 col-sm-6">
        <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '15px' }}>
          <div className="card-body text-center p-4">
            <div className="d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
              style={{ 
                width: '60px', 
                height: '60px',
                backgroundColor: planColors.secondary,
                border: `2px solid ${planColors.primary}`
              }}>
              <i 
                className={`${quotaDisplay.icon} fa-lg`}
                style={{ color: quotaDisplay.customColor || `var(--bs-${quotaDisplay.color})` }}
              ></i>
            </div>
            <h3 
              className="fw-bold mb-1" 
              style={{ color: quotaDisplay.customColor || `var(--bs-${quotaDisplay.color})` }}
            >
              {quotaDisplay.text}
            </h3>
            <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
              {quotaDisplay.subtext}
            </p>
            {quotaDisplay.showUpgrade && (
              <Link 
                to="/pricing" 
                className="btn btn-sm mt-2"
                style={{ 
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  fontSize: '0.75rem',
                  padding: '0.25rem 0.75rem'
                }}
              >
                <i className="fas fa-crown me-1"></i>
                Upgrade
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuickStats;